import { useState, FormEvent } from 'react';

export default function LeadForm() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      await fetch('https://readdy.ai/api/form/d795bhadqn9mib2tqvog', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-white">
      <div className="max-w-6xl mx-auto px-6 md:px-14">
        <div className="flex flex-col lg:flex-row gap-16 items-start">

          {/* Left: copy + trust signals */}
          <div className="w-full lg:w-2/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#C9A961]"></div>
              <span className="text-[#C9A961] text-xs font-semibold uppercase tracking-[0.2em]">Request a Quote</span>
            </div>
            <h2
              className="text-4xl md:text-5xl font-bold text-[#1A1A1A] leading-tight mb-5"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Schedule Your
              <br />
              Inspection
            </h2>
            <p className="text-[#1A1A1A]/52 text-base leading-relaxed mb-10">
              Fill out the form and we&apos;ll match you with a certified inspector in your area. Fast response, no obligation.
            </p>

            <div className="flex flex-col gap-4 mb-10">
              {[
                { icon: 'ri-shield-check-line', text: 'Licensed & certified inspectors, TX' },
                { icon: 'ri-timer-flash-line', text: 'Same-day and next-day scheduling available' },
                { icon: 'ri-file-list-3-line', text: 'Detailed digital reports with photos' },
                { icon: 'ri-star-line', text: 'Reliable, professional service experience' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-[#C9A961]/10 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-[#C9A961] text-sm`}></i>
                  </div>
                  <span className="text-sm text-[#1A1A1A]/68">{item.text}</span>
                </div>
              ))}
            </div>

            {/* Call block */}
            <div className="bg-[#1A1A1A] rounded-xl p-6">
              <p className="text-white/45 text-xs uppercase tracking-wider mb-2">Prefer to call?</p>
              <a
                href="tel:+12814217355"
                className="text-[#C9A961] text-2xl font-bold hover:text-[#b8944f] transition-colors block"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                (281) 421-7355
              </a>
            </div>
          </div>

          {/* Right: form — 4 fields only */}
          <div className="w-full lg:w-3/5 bg-[#F5F4F1] rounded-2xl p-8 md:p-10">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-[#C9A961]/12 rounded-full mb-5">
                  <i className="ri-check-line text-[#C9A961] text-3xl"></i>
                </div>
                <h3
                  className="text-2xl font-bold text-[#1A1A1A] mb-3"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Request Received
                </h3>
                <p className="text-[#1A1A1A]/52 text-base max-w-sm leading-relaxed">
                  Thank you. A member of our team will contact you shortly to confirm your inspection details.
                </p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="flex flex-col gap-5">
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A]/55 uppercase tracking-wider mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="John Smith"
                    className="w-full h-12 px-4 text-sm bg-white border border-[#1A1A1A]/10 rounded-lg focus:outline-none focus:border-[#C9A961] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A]/55 uppercase tracking-wider mb-2">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="(555) 000-0000"
                    className="w-full h-12 px-4 text-sm bg-white border border-[#1A1A1A]/10 rounded-lg focus:outline-none focus:border-[#C9A961] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A]/55 uppercase tracking-wider mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="w-full h-12 px-4 text-sm bg-white border border-[#1A1A1A]/10 rounded-lg focus:outline-none focus:border-[#C9A961] transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A]/55 uppercase tracking-wider mb-2">
                    Property Address *
                  </label>
                  <input
                    type="text"
                    name="property_address"
                    required
                    placeholder="123 Main St, City, State ZIP"
                    className="w-full h-12 px-4 text-sm bg-white border border-[#1A1A1A]/10 rounded-lg focus:outline-none focus:border-[#C9A961] transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#C9A961] text-white font-semibold text-base rounded-lg hover:bg-[#b8944f] transition-colors duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 py-4 mt-1"
                >
                  {loading ? 'Submitting...' : 'Request Inspection'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
